import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  EventEmitter,
  Output,
  ViewChild,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { Option } from '@interfaces/common/option';
import {
  LinkMeetingRequest,
  MeetingLinkSelection,
} from '@interfaces/meetings/requests/meeting.request';
import { MeetingDto } from '@interfaces/meetings/responses/meeting.response';
import { TranslateService } from '@ngx-translate/core';
import { ToastHelperService } from '@services/helpers/toast-helper.service';
import { MeetingsHttpService } from '@services/http/meetings/meetings-http.service';
import { VaultBaseComponent } from '@src/app/shared/components/base/vault-base.component';
import { DialogComponent } from '@src/app/shared/components/dialog/dialog.component';
import { SharedModule } from '@src/app/shared/shared.module';
import { first } from 'rxjs';
import { MeetingLinkFieldsComponent } from '@features/dashboard/meetings/components/meeting-link-fields/meeting-link-fields.component';
import { mensagemDoErro } from '@features/dashboard/meetings/components/meeting-presenter';

/**
 * Vincular uma ligação à mão (card 3138): empresa → parceiro → deal e relacionamento bancário →
 * contato (`app-meeting-link-fields`). Vale para qualquer ligação, inclusive as da agenda. O que já
 * estava vinculado é ignorado pelo backend.
 *
 * "Empresas associadas" (card 3222): select múltiplo só de empresas, sem parceiro nem deal, para as
 * várias empresas de que a call tratou. Abre com as que a ligação já tem (vínculo do tipo empresa) e
 * vai para o backend só quando muda, como a lista final: desmarcar desfaz o vínculo.
 *
 * Substitui a antiga "Vincular a um deal": o deal agora é um dos campos da cascata.
 */
@Component({
  selector: 'app-modal-link-meeting',
  standalone: true,
  imports: [CommonModule, FormsModule, SharedModule, MeetingLinkFieldsComponent],
  templateUrl: './modal-link-meeting.component.html',
  styleUrl: './modal-link-meeting.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ModalLinkMeetingComponent extends VaultBaseComponent {
  @ViewChild('dialog') dialogRef: DialogComponent | null = null;
  @ViewChild('campos') campos: MeetingLinkFieldsComponent | null = null;

  /** A ligação já com os vínculos novos — a tela troca a linha. */
  @Output() vinculou = new EventEmitter<MeetingDto>();

  protected readonly ligacao = signal<MeetingDto | null>(null);
  protected readonly gravando = signal(false);
  protected readonly selecao = signal<MeetingLinkSelection>({});

  /** Empresas do tenant, para as "Empresas associadas" (carregadas na primeira abertura). */
  protected readonly empresas = signal<Option[]>([]);
  protected readonly carregandoEmpresas = signal(false);
  /** O que a ligação já tinha ao abrir e o que está marcado agora (ids de empresa). */
  private readonly associadasIniciais = signal<string[]>([]);
  protected readonly associadas = signal<string[]>([]);

  /**
   * Empresas já associadas que o select não mostra (fora do escopo de empresas do header, ou ainda
   * carregando). O `app-select` descarta do valor o que não está nas opções; elas voltam no pedido e
   * não contam como mudança, senão o "Vincular" desfaria um vínculo que o usuário nem viu.
   */
  private readonly associadasOcultas = computed(() => {
    const visiveis = new Set(this.empresas().map(e => e.value));
    return this.associadasIniciais().filter(id => !visiveis.has(id));
  });

  /** A lista final: o que está marcado mais as ocultas. */
  private readonly associadasFinais = computed(() => [
    ...new Set([...this.associadas(), ...this.associadasOcultas()]),
  ]);

  /** As "Empresas associadas" mudaram em relação ao que a ligação tinha. */
  protected readonly associadasMudaram = computed(() => {
    const antes = new Set(this.associadasIniciais());
    const agora = new Set(this.associadasFinais());
    return antes.size !== agora.size || [...agora].some(id => !antes.has(id));
  });

  private readonly destroyRef = inject(DestroyRef);

  constructor(
    translateService: TranslateService,
    private readonly meetingsService: MeetingsHttpService,
    private readonly toast: ToastHelperService
  ) {
    super(translateService);
  }

  open(meeting: MeetingDto): void {
    this.ligacao.set(meeting);
    this.gravando.set(false);
    this.selecao.set({});
    const jaAssociadas = [
      ...new Set(meeting.links.filter(l => l.kind === 'Company').map(l => l.companyId)),
    ];
    this.associadasIniciais.set(jaAssociadas);
    this.associadas.set(jaAssociadas);
    this.carregarEmpresas();
    this.campos?.reiniciar();
    this.dialogRef?.open('', '', '', 'Cancelar', '', 'link-meeting', false, true);
  }

  isFormValid(): boolean {
    const s = this.selecao();
    return (
      !!this.ligacao() &&
      !this.gravando() &&
      (!!s.companyId || !!s.dealId || this.associadasMudaram())
    );
  }

  protected aoMudarAssociadas(ids: string[] | null): void {
    this.associadas.set(ids ?? []);
  }

  submitAction(): void {
    const meeting = this.ligacao();
    if (!meeting || !this.isFormValid()) return;

    this.gravando.set(true);
    this.meetingsService
      .linkMeeting(meeting.id, this.pedido())
      .pipe(first(), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: atualizada => {
          this.gravando.set(false);
          this.toast.showToastSuccess('Reunião vinculada.');
          if (atualizada) this.vinculou.emit(atualizada);
          this.fechar();
        },
        error: erro => {
          this.gravando.set(false);
          this.toast.showToastError(
            'Não foi possível vincular',
            mensagemDoErro(erro, 'Tente de novo em alguns instantes.')
          );
        },
      });
  }

  fechar(): void {
    this.dialogRef?.close();
  }

  /** A cascata e, só quando mudaram, as "Empresas associadas" (lista final). */
  private pedido(): LinkMeetingRequest {
    return this.associadasMudaram()
      ? { ...this.selecao(), associatedCompanyIds: this.associadasFinais() }
      : this.selecao();
  }

  private carregarEmpresas(): void {
    if (this.empresas().length || this.carregandoEmpresas()) return;

    this.carregandoEmpresas.set(true);
    this.meetingsService
      .getLinkOptions()
      .pipe(first(), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: opcoes => {
          this.carregandoEmpresas.set(false);
          this.empresas.set((opcoes?.companies ?? []).map(c => ({ label: c.name, value: c.id })));
        },
        error: erro => {
          this.carregandoEmpresas.set(false);
          this.toast.showToastError(
            'Não foi possível carregar as empresas',
            mensagemDoErro(erro, 'Tente de novo em alguns instantes.')
          );
        },
      });
  }
}
